import React from 'react';
import { cn } from '@/lib/utils';
import { KPICard, KPICardProps } from '../KPICard/KPICard';
import { WeatherWidget, WeatherWidgetProps } from '../WeatherWidget/WeatherWidget';
import { PixieHelperCard, PixieHelperCardProps } from '../PixieHelperCard/PixieHelperCard';

// --- TYPE DEFINITIONS ---

interface DashboardWidgetProps {
  patientCount?: string;
  patientLabel?: string;
  kpis?: KPICardProps[];
  weather?: WeatherWidgetProps;
  pixieHelper?: PixieHelperCardProps;
  onKPIClick?: (kpi: KPICardProps) => void;
  onWeatherClick?: () => void;
  onPixieClick?: () => void;
  className?: string;
}

// --- DEFAULT DATA GENERATORS ---

const getDefaultKPIs = (): KPICardProps[] => [
  {
    title: 'Tasks Completed',
    value: 34,
    total: 48,
    percentage: 71,
    subtitle: '14 remaining',
    color: 'blue',
  },
  {
    title: 'Insurance Verified',
    value: 10,
    total: 12,
    percentage: 83,
    subtitle: '2 pending reviews',
    color: 'green',
  },
  {
    title: 'Recall Slots',
    value: 3,
    total: 6,
    percentage: 50,
    subtitle: '3 open slots',
    color: 'orange',
  },
];

const getDefaultWeather = (): WeatherWidgetProps => ({
  temperature: 85,
  location: 'Orlando, FL',
  description: 'Partly Cloudy',
  condition: 'partly-cloudy',
});

const getDefaultPixieHelper = (): PixieHelperCardProps => ({
  title: 'Pixie AI Helper',
  subtitle: 'Pixie AI focuses on tasks, so you can focus on patients',
  status: 'Completed',
  processingTasks: [],
  queuedTasks: [],
  completedTasks: [],
});

// --- MAIN COMPONENT ---

const DashboardWidget: React.FC<DashboardWidgetProps> = ({
  patientCount = '104',
  patientLabel = 'Patients today',
  kpis: providedKPIs,
  weather: providedWeather,
  pixieHelper: providedPixieHelper,
  onKPIClick,
  onWeatherClick,
  onPixieClick,
  className = '',
}) => {
  const kpis = providedKPIs && providedKPIs.length > 0 ? providedKPIs : getDefaultKPIs();
  const weather = providedWeather && Object.keys(providedWeather).length > 0 ? providedWeather : getDefaultWeather();
  const pixieHelper = providedPixieHelper && Object.keys(providedPixieHelper).length > 0 ? providedPixieHelper : getDefaultPixieHelper();

  return (
    <div className={cn('p-6 bg-gray-50 rounded-lg', className)}>
      <div className="mb-6">
        <h1 className="text-4xl font-bold text-gray-800">{patientCount}</h1>
        <p className="text-md text-gray-500">{patientLabel}</p>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 xl:grid-cols-12 gap-6">
        {kpis.map((kpi, index) => (
          <div key={index} className="col-span-1 md:col-span-1 lg:col-span-1 xl:col-span-3">
            <KPICard {...kpi} onClick={() => onKPIClick?.(kpi)} />
          </div>
        ))}

        <div className="col-span-1 md:col-span-1 lg:col-span-2 xl:col-span-3">
          <WeatherWidget {...weather} onClick={onWeatherClick} />
        </div>

        <div className="col-span-2 md:col-span-3 lg:col-span-5 xl:col-span-6">
          <PixieHelperCard {...pixieHelper} />
        </div>
      </div>
    </div>
  );
};

DashboardWidget.displayName = 'DashboardWidget';

export { DashboardWidget };
export type { DashboardWidgetProps };
